import { FC, useState } from "react";
import { Font,  Page, Text, View, Document, StyleSheet, Image, pdf } from "@react-pdf/renderer";
import { WLocation } from "@/types/inventoryV2";
import { PrinterOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { App } from 'antd';
import { useTranslation } from "react-i18next";
import QRCode from 'qrcode';
import { getLocationIdx } from "@/utils/wLocation";
import { codeObjToStr, TagType } from "@/utils/codeParse";
import fontSrc from '@/assets/fonts/msyh.ttf';
import vnSrc from '@/assets/fonts/NotoSans-Regular.ttf';
import krSrc from '@/assets/fonts/NotoSansKR-Regular.otf';

// 注册支持中文的字体（重复注册幂等；若启动预热失败，首次渲染时在此兜底加载）
Font.register({
  family: "NotoSansSC",
  src: fontSrc
});

Font.register({
  family: "VnFontSrc",
  src: vnSrc
});

Font.register({
  family: "KoFontSrc",
  src: krSrc
});

// 定义 PDF 文档样式
const styles = StyleSheet.create({
  page: {
    position: "relative",
    fontFamily: ["VnFontSrc", "KoFontSrc", "NotoSansSC"],
    padding: 40,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  },
  qrContainer: {
    flex: 1,
    width: "100%",
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  },
  qrCode: {
    width: 400,
    height: 400
  },
  locationNumber: {
    fontSize: 28,
    marginTop: 10,
    textAlign: 'center' as any,
    fontWeight: 'bold',
    letterSpacing: 2
  }
});

interface PrintLocationProps {
  datas: WLocation[];
  onStart?: () => void;
  onEnd?: () => void;
  disabled?: boolean;
}

const PrintLocation: FC<PrintLocationProps> = ({ datas, onStart, onEnd, disabled = false }) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const [loading, setLoading] = useState<boolean>(false);

  // 生成二维码
  const handleQRCode = (text: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!text) {
        resolve('');
        return;
      }
      let codeStr = codeObjToStr({
        tt: TagType.LOCATION,
        ln: text,
        p: '',
        l: '',
        t: '',
        c: '',
        i: '',
        u: '',
        r: '',
        s: '',
        o: '',
        po: '',
      });
      QRCode.toDataURL(codeStr, {
        width: 800,
        margin: 0,
        errorCorrectionLevel: 'M'
      }, (err, url) => {
        if (err) {
          reject(err);
        } else {
          resolve(url);
        }
      });
    });
  };

  // 处理PDF打印
  const handlePDF = async () => {
    if (!datas || datas.length === 0) {
      notification.error({
        placement: 'top',
        message: t('purchase.printErr'),
        description: t('bom.errTip')
      });
      return;
    }

    setLoading(true);
    if (onStart) onStart();

    try {
      // 为每个库位生成二维码URL
      const qrCodeUrls: string[] = [];
      for (let i = 0; i < datas.length; i++) {
        const location = datas[i];
        try {
          const qrUrl = await handleQRCode(location.number);
          qrCodeUrls.push(qrUrl);
        } catch (error) {
          console.error('二维码生成失败:', location.number, error);
          qrCodeUrls.push('');
        }
      }

      // 生成PDF文档
      const pageElement = (
        <Document>
          {qrCodeUrls.map((qrUrl, index) => (
            <Page size="A4" style={styles.page} key={index} orientation="landscape">
              <View style={styles.qrContainer}>
                {qrUrl && (
                  <>
                    <Image src={qrUrl} style={styles.qrCode} />
                    <Text style={styles.locationNumber}>{getLocationIdx(datas[index])}</Text>
                  </>
                )}
              </View>
            </Page>
          ))}
        </Document>
      );

      // 将文档转换为Blob
      const blob = await pdf(pageElement).toBlob();
      const pdfUrl = URL.createObjectURL(blob);

      // 创建隐藏的iframe用于打印
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = pdfUrl;

      // 将iframe添加到body中
      document.body.appendChild(iframe);

      iframe.onload = () => {
        console.log('PDF 加载完成，等待额外时间');
        try {
          console.log('最终调用 print()');
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch (error) {
          console.error('打印失败:', error);
          notification.error({ placement: 'top', message: '打印失败' });
        } finally {
          setLoading(false);
          if (onEnd) onEnd();
        }
      };

    } catch (error) {
      console.error('生成PDF失败:', error);
      notification.error({ placement: 'top', message: '生成PDF失败' });
      setLoading(false);
      if (onEnd) onEnd();
    }
  };

  return (
    <Button
      type="primary"
      icon={<PrinterOutlined />}
      onClick={handlePDF}
      loading={loading}
      disabled={disabled}
    >
      {t('common.printInfo')}
    </Button>
  );
};

export default PrintLocation;
